import { RefObject, useEffect, useState } from "react";

import {
    dropTargetForElements,
    type ElementDropTargetEventBasePayload,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";

import { DropData, Edge } from "../types";
import { canPlaceAt, isDragData, isDropData } from "../utils";

interface UseItemDropTargetArgs {
    itemRef: RefObject<HTMLElement | null>;
    listId: string;
    index: number;
}

export function useItemDropTarget({
    itemRef,
    listId,
    index,
}: UseItemDropTargetArgs): Edge | null {
    const [dropEdge, setDropEdge] = useState<Edge | null>(null);

    // Register the drop target; cleanup runs on unmount or when deps change.
    useEffect(() => {
        const item = itemRef.current;
        if (!item) {
            return;
        }
        const updateEdge = ({
            self,
            source,
        }: ElementDropTargetEventBasePayload) => {
            if (!isDropData(self.data) || !isDragData(source.data)) {
                setDropEdge(null);
                return;
            }
            setDropEdge(
                canPlaceAt(source.data, self.data) ? self.data.edge : null
            );
        };
        return dropTargetForElements({
            element: item,
            canDrop: ({ source }) =>
                isDragData(source.data) && source.data.listId === listId,
            getData: ({ input, element }): Record<string, unknown> => {
                const rect = element.getBoundingClientRect();
                const edge: Edge =
                    input.clientY < rect.top + rect.height / 2
                        ? "top"
                        : "bottom";
                const data: DropData = { listId, index, edge };
                return { ...data };
            },
            onDragEnter: updateEdge,
            onDrag: updateEdge,
            onDragLeave: () => setDropEdge(null),
            onDrop: () => setDropEdge(null),
        });
    }, [itemRef, listId, index]);

    return dropEdge;
}
